/* QueryViewComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const Renderer = require("./QueryViewRenderer");

class QueryViewComponent extends EventEmitter {
    constructor(data) {
        super();
        this._device = data.device;
        this._deviceList = data.deviceList;
        this._devices = data.deviceList;
        this._deviceResultMap = data.deviceResultMap;
        this._query = data.query;
        this._searchType = data.searchType;
        this._jobEntityDef = data.jobEntityDef;
        this._jobIdMap = data.jobIdMap;
        this._deviceConfigFeed = undefined;
        this._rawConfig = undefined;
        this._matches = data.matches;
        this._parentRenderer = data.parentRenderer;
        this._currentResultEntity = data.selectedResultEntity;
        this._allConfigurations = "";
        this._searchMethod = data.searchMethod;
        this._conceptItem = data.conceptItem;
        this._showCommand = data.showCommand;
        this._deviceOptions = [];
        this.activeOrgId = _.get(data.parentRenderer, "_model.activeOrgId", null);

        this._renderer = new Renderer(this);
    }

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        return this.renderer.shutdown().then(() => {
            this._device = undefined;
            this._renderer = undefined;
            this._query = undefined;
            this._searchType = undefined;
            this._deviceConfigFeed = undefined;
            this._rawConfig = undefined;
            this._deviceOptions = undefined;
            this._devices = undefined;
            this.activeOrgId = undefined;
            this._jobEntityDef = undefined;
            this._allConfigurations = undefined;
            this._jobIdMap = undefined;
            this._rawConfigBroken = undefined;
            this._commandListOptions = undefined;
        });
    }

    async whenModelIsReady() {
        await this._getConfig();
        return await this.getDeviceOptions();
    }

    _getDeviceCapturedConfig() {
        return Promise.resolve()
            .then(() => {
                return this.getCapturedConfig();
            })
            .then((job) => {
                let currentAdHocEntity;
                if (job === "NA") {
                    this._rawConfig = "Query still running...";
                    return Promise.resolve();
                }
                if (this._parentRenderer._selectedSavedQuery === " Last Run") {
                    if (
                        !this._parentRenderer._model._adHocQueriesMap[
                            this._parentRenderer._selectedSavedQuery
                        ]
                    ) {
                        currentAdHocEntity =
                            this._parentRenderer._model._adHocQueriesMap[
                                this._parentRenderer._model._proxyAdHocQueryEntity._tenantId
                            ];
                    } else {
                        currentAdHocEntity =
                            this._parentRenderer._model._adHocQueriesMap[
                                this._parentRenderer._selectedSavedQuery
                            ];
                    }
                } else {
                    currentAdHocEntity =
                        this._parentRenderer._model._adHocQueriesMap[
                            this._parentRenderer._selectedSavedQuery
                        ];
                    // if(_.isEmpty(currentAdHocEntity)) {
                    //     currentAdHocEntity = Object.values(this._parentRenderer._model._adHocQueriesMap).find(i => i.getName() === this._parentRenderer._selectedSavedQuery);
                    // }
                }
                if (currentAdHocEntity.getFilter() === "NONE") {
                    const jobEntity = job.find((i) => i.getJobTypeName() === "capture");

                    if (jobEntity) {
                        this._rawConfig = _.get(
                            jobEntity.getFinishObj(),
                            "capturedConfig",
                            _.get(
                                jobEntity.getFinishObj(),
                                "deviceConfig",
                                "No Configuration Available"
                            )
                        );
                    } else {
                        this._rawConfig = "No Configuration Available";
                    }
                }
                return Promise.resolve();
            });
    }

    async _getDeviceConceptConfig() {
        const job = await this.getCapturedConfig();
        const jobEntity = job.find((i) => i.getJobTypeName() === "extractConcept");
        if (jobEntity) {
            this._rawConfig = _.get(jobEntity.getFinishObj(), "parsedConfig", {});
            if (!_.isEmpty(this._rawConfig)) {
                const confObj = jobEntity.getFinishObj()["parsedConfig"];
                const mainLevel = Object.keys(confObj);
                let output = "";

                mainLevel.forEach((first) => {
                    // output += `\n${first}\n`;
                    if (typeof confObj[first] === "object") {
                        Object.keys(confObj[first]).forEach((second) => {
                            // output += `\n${second}`;
                            output += `\n\n${confObj[first][second].join("\n")}\n`;
                        });
                    }
                });

                this._rawConfig = output;
            }
        }
        return Promise.resolve();
    }

    _getConfig() {
        return Promise.resolve().then(() => {
            this._deviceConfigFeed =
                this._currentResultEntity.getQueriedData() || "No Configuration Available";
            this._deviceConfigFeed = this._deviceConfigFeed.replace(/(\/r)?\/n/g, "\n");
            this._rawConfig = this._deviceConfigFeed;

            this._rawConfigBroken = this._createCommandList(this._rawConfig);
            return Promise.resolve();
        });
    }

    async getCapturedConfig() {
        if (!_.isEmpty(this._currentResultEntity.getJobIds())) {
            return this._jobEntityDef.loadJobs(
                this.activeOrgId,
                {id: {$in: this._currentResultEntity.getJobIds()}},
                {}
            );
        } else {
            return Promise.resolve("NA");
        }
    }

    getDeviceOptions() {
        return Promise.resolve()
            .then(() => {
                this._devices = this._deviceList;
                return this._devices;
            })
            .then((deviceEntities) => {
                this._deviceOptions = deviceEntities.map((device) => {
                    let typeBaseGroupMap = device.getTypeBaseGroupMap();
                    let detectedTypeBase = device.getDetectedTypeBase();
                    const _icon = document.createElement("base-icon-gl");

                    _icon.classList.add("type-icon");
                    if (detectedTypeBase === "unknown") {
                        _icon.dataset.icon = "common.default_device";
                        _icon.setAttribute("title", "Unknown");
                    } else {
                        _icon.dataset.icon =
                            "common." +
                            _.snakeCase(typeBaseGroupMap[detectedTypeBase] || "Unknown");
                        _icon.setAttribute("title", detectedTypeBase);
                    }
                    return {label: `${_icon.outerHTML} ${device.getName()}`, value: device.getId()};
                });
            });
    }

    _setCurrentResultEntity() {
        this._currentResultEntity = this._deviceResultMap[this._device.getId()];
    }

    _createCommandList(inputString) {
        const commands = {};
        let currentCommand = null;
        let allConfigurations = "";

        inputString.split("\n").forEach((line) => {
            line = line.trim();

            if (line.startsWith("Command:")) {
                if (currentCommand !== null) {
                    commands[currentCommand.command_name] = currentCommand.command_val.join("\n");
                }

                currentCommand = {
                    command_name: line.replace("Command:", "").trim(),
                    command_val: [],
                };
            } else if (currentCommand !== null) {
                currentCommand.command_val.push(line);

                // Exclude lines with "command:"
                if (!line.toLowerCase().includes("command:")) {
                    allConfigurations += line + "\n";
                }
            }
        });

        // Push the last command into the list
        if (currentCommand !== null) {
            commands[currentCommand.command_name] = currentCommand.command_val.join("\n");
        }

        const _icon = document.createElement("base-icon-gl");
        _icon.dataset.icon = "common.code";
        _icon.classList.add("code-icon");

        this._commandListOptions = Object.keys(commands).map((com) => {
            return {
                label: `${_icon.outerHTML} ${com} (${this._countOccurrences(
                    commands[com],
                    this._query
                )})`,
                value: com,
            };
        });
        this._commandListOptions.unshift({
            label: `${_icon.outerHTML} All Results`,
            value: "all",
        });
        this._allConfigurations = allConfigurations.trim() || inputString;

        return commands;
    }

    _countOccurrences(configText, targetWord) {
        const regex = new RegExp(targetWord, "g");
        const matches = configText.match(regex);

        if (matches) {
            return matches.length;
        } else {
            return 0;
        }
    }
}

module.exports = QueryViewComponent;
